// ============================================================
// TMR Portal Admin — vincular um usuário ao seu login no sistema legado (05 I7)
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn, IconTile } from '../../components/primitives';
import { SearchBox } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useLegacyUserSearch, useLinkLegacyUser, useUnlinkLegacyUser } from '../../data/users/queries';
import type { LegacyUserCandidate, UserListItem } from '../../data/users/contract';
import {
  MIN_LEGACY_SEARCH_LENGTH,
  candidateState,
  isEmailMatch,
  takenByLabel,
} from '../../data/users/legacyLink';
import type { CandidateState } from '../../data/users/legacyLink';
import { useDebounced } from '../../useDebounced';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link LinkLegacyUserModal}. */
interface LinkLegacyUserModalProps {
  /** The account being correlated. Its e-mail seeds the first search, and its `legacyUserId` is the current state. */
  user: UserListItem;
  onClose: () => void;
}

/**
 * Correlates an operator to their login in the legacy system, by hand.
 *
 * Creating a user already tries this automatically by e-mail; this modal is what the operator reaches for
 * when that could not decide — nobody matched, more than one did, the id was taken, or the legacy was
 * unreachable. It opens **already searching by the account's e-mail**, so the common case is one click.
 *
 * Why it matters, since nothing on screen would otherwise say: an account with no legacy id replicates
 * `produtores.gestor_conta` as null the day it is named Gestor da Conta of an Empresa (04 §3) — no error,
 * no warning, just a column that stays empty in the old system.
 */
export function LinkLegacyUserModal({ user, onClose }: LinkLegacyUserModalProps) {
  // Seeded with the e-mail: the "tenta bater por e-mail" step of the flow is just this search, run for the
  // operator instead of by them.
  const [search, setSearch] = useState(user.email);
  const [pendingId, setPendingId] = useState<number | null>(null);

  const debouncedSearch = useDebounced(search);
  const results = useLegacyUserSearch(debouncedSearch);
  const link = useLinkLegacyUser();
  const unlink = useUnlinkLegacyUser();

  const stop = (e: MouseEvent) => e.stopPropagation();
  const writeError = (link.error ?? unlink.error) instanceof ApiError
    ? ((link.error ?? unlink.error) as ApiError)
    : null;

  const runLink = (candidate: LegacyUserCandidate) => {
    setPendingId(candidate.legacyUserId);
    link.mutate({ id: user.id, legacyUserId: candidate.legacyUserId }, { onSuccess: onClose });
  };

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 680, maxWidth: '100%', maxHeight: '82vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 26px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <div className="pa-eyebrow" style={{ marginBottom: 4 }}>Vínculo com o legado</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>
                {user.name}
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--fg-muted)', maxWidth: 470, lineHeight: 1.5 }}>
                Aponta esta pessoa para o cadastro dela no sistema legado. É o que permite registrá-la como
                Gestor da Conta de uma empresa — sem o vínculo, esse campo vai vazio para o legado.
              </p>
            </div>
            <button type="button" onClick={onClose} className="pa-icon-btn" style={{ width: 34, height: 34, flexShrink: 0 }}>
              <StrokeIcon d={Icons.x} size={18} />
            </button>
          </div>

          {user.legacyUserId !== null && (
            <CurrentLink
              legacyUserId={user.legacyUserId}
              unlinking={unlink.isPending}
              onUnlink={() => unlink.mutate(user.id, { onSuccess: onClose })}
            />
          )}

          <div style={{ padding: '18px 26px 0' }}>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="Nome, e-mail, login ou #id no legado…"
              width="100%"
            />
            {/* Dito de frente, porque é a primeira coisa que alguém tenta: o cadastro de usuários do legado
                não tem coluna de CPF, então não há como buscar por ele (05 §10). */}
            <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--fg-subtle)', lineHeight: 1.5 }}>
              A busca traz apenas usuários da equipe interna no legado. Não é possível buscar por CPF — o
              cadastro de usuários do legado não guarda esse dado.
            </div>
            {writeError && (
              <div role="alert" style={{ marginTop: 14, padding: '12px 14px', background: 'oklch(96% 0.03 28)', border: '1px solid oklch(80% 0.09 28)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'oklch(38% 0.12 28)' }}>
                {writeError.detail}
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 26px 26px', minHeight: 220 }}>
            <LegacyResults
              search={debouncedSearch}
              seededEmail={user.email}
              results={results}
              currentLegacyUserId={user.legacyUserId}
              pendingId={pendingId}
              linking={link.isPending}
              onLink={runLink}
            />
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}

/** Props for {@link CurrentLink}. */
interface CurrentLinkProps {
  legacyUserId: number;
  unlinking: boolean;
  onUnlink: () => void;
}

/**
 * The link this account already holds, and the way out of a wrong one.
 *
 * Unlinking is offered here rather than hidden behind the list because it is the only way to correct a
 * mistake: the legacy id is unique, so the rightful owner cannot take it while this account holds it.
 */
function CurrentLink({ legacyUserId, unlinking, onUnlink }: CurrentLinkProps) {
  return (
    <div style={{ margin: '18px 26px 0', padding: 14, background: 'var(--cream-100)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: 'var(--ink-900)' }}>
          Vinculada ao legado{' '}
          <span style={{ fontFamily: 'var(--font-mono)' }}>#{legacyUserId}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.5 }}>
          Para apontar para outro cadastro, desvincule primeiro. Se esta pessoa for Gestor da Conta de
          alguma empresa, esse campo passa a ir vazio para o legado.
        </div>
      </div>
      <Btn variant="secondary" onClick={unlinking ? undefined : onUnlink} disabled={unlinking}>
        {unlinking ? 'Desvinculando…' : 'Desvincular'}
      </Btn>
    </div>
  );
}

/** Props for {@link LegacyResults}. */
interface LegacyResultsProps {
  search: string;
  /** The account's e-mail, which seeded the first search — what makes a single hit an e-mail match. */
  seededEmail: string;
  results: ReturnType<typeof useLegacyUserSearch>;
  currentLegacyUserId: number | null;
  pendingId: number | null;
  linking: boolean;
  onLink: (candidate: LegacyUserCandidate) => void;
}

/** The search results, plus every state a search can be in. */
function LegacyResults({
  search,
  seededEmail,
  results,
  currentLegacyUserId,
  pendingId,
  linking,
  onLink,
}: LegacyResultsProps) {
  if (search.trim().length < MIN_LEGACY_SEARCH_LENGTH) {
    return (
      <EmptyState
        message="Busque o cadastro desta pessoa no legado."
        hint={`Digite ao menos ${MIN_LEGACY_SEARCH_LENGTH} caracteres — nome, e-mail, login ou o número do cadastro.`}
      />
    );
  }
  if (results.isPending) return <Skeleton rows={4} />;

  // O endpoint só existe onde há conexão com o legado, então 404 aqui é "ambiente sem legado", não
  // "nada encontrado" — dizer as duas coisas igual mandaria o operador procurar um erro que não existe.
  if (results.isError) {
    const notAvailable = results.error instanceof ApiError && results.error.status === 404;
    return notAvailable ? (
      <EmptyState
        message="Busca no legado indisponível neste ambiente."
        hint="Este ambiente não está conectado ao sistema legado."
      />
    ) : (
      <ErrorState error={results.error} onRetry={() => void results.refetch()} />
    );
  }

  if (results.data.length === 0) {
    const searchedTheEmail = search.trim().toLowerCase() === seededEmail.toLowerCase();
    return (
      <EmptyState
        message={
          searchedTheEmail
            ? 'Nenhum cadastro no legado com este e-mail.'
            : 'Nenhum cadastro encontrado no legado.'
        }
        hint={
          searchedTheEmail
            ? 'Busque pelo nome, pelo login ou pelo número do cadastro.'
            : 'Revise o termo buscado.'
        }
      />
    );
  }

  // Um único resultado para a busca semeada com o e-mail é o casamento automático que a criação tentaria:
  // vale marcar, porque é o que dá confiança para clicar sem conferir campo a campo.
  const emailMatch = isEmailMatch(search, seededEmail, results.data.length);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {emailMatch && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--forest-800)' }}>
          <StrokeIcon d={Icons.check} size={14} />
          Encontrado pelo e-mail da conta.
        </div>
      )}
      {results.data.map((candidate) => (
        <CandidateRow
          key={candidate.legacyUserId}
          candidate={candidate}
          state={candidateState(candidate, currentLegacyUserId)}
          busy={linking && pendingId === candidate.legacyUserId}
          linking={linking}
          onLink={() => onLink(candidate)}
        />
      ))}
    </div>
  );
}

/** Props for {@link CandidateRow}. */
interface CandidateRowProps {
  candidate: LegacyUserCandidate;
  state: CandidateState;
  /** True while this very row is being linked, so only its own button says so. */
  busy: boolean;
  /** True while any row is being linked — nothing else should be clickable meanwhile. */
  linking: boolean;
  onLink: () => void;
}

/** One legacy login, with the reason it cannot be picked when that is the case. */
function CandidateRow({ candidate, state, busy, linking, onLink }: CandidateRowProps) {
  const takenByOther = state === 'takenByOther';
  const pickable = state === 'available' && !linking;

  return (
    <div
      className="pa-card"
      style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, opacity: takenByOther ? 0.6 : 1 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <IconTile size={36}>
          <StrokeIcon d={Icons.users} size={17} />
        </IconTile>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {candidate.name || '(sem nome no legado)'}
            {!candidate.active && (
              <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--fg-subtle)', fontWeight: 400 }}>
                inativo no legado
              </span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
            {candidate.email}
            {candidate.login && candidate.login !== candidate.email && ` · ${candidate.login}`}
            {' · '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>#{candidate.legacyUserId}</span>
          </div>
          {takenByOther && (
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>
              {takenByLabel(candidate)}
            </div>
          )}
        </div>
      </div>
      {state === 'current' ? (
        <span style={{ fontSize: 12.5, color: 'var(--forest-800)', whiteSpace: 'nowrap' }}>Vinculado</span>
      ) : (
        <Btn variant="secondary" onClick={pickable ? onLink : undefined} disabled={!pickable}>
          {busy ? 'Vinculando…' : 'Vincular'}
        </Btn>
      )}
    </div>
  );
}
